import { FC } from 'react';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { useIntl } from 'react-intl';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { AddItemSelect } from '../../components/AddItemSelect';
import { useModalContext } from '../../../../../modal/ModalContext';
import { useSnackbar } from '../../../../../store/snackbar';

const listMetricQuery = graphql(`
	query addMetricSelect_listMetric {
		listContentMetric(orderBy: [{ metric: { name: asc } }]) {
			id
			metric {
				name
			}
		}
	}
`);

const updateActivityMutation = graphql(`
	mutation addMetricSelect_updateActivity($activityId: UUID!, $data: ActivityUpdateInput!) {
		updateActivity(by: { id: $activityId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	activityId: string;
	contentMetricIds: string[];
}>;

export const AddMetricSelect: FC<Props> = ({ activityId, contentMetricIds }) => {
	const intl = useIntl();
	const { isLoading, data } = useApiQuery(listMetricQuery);
	const { setModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	// filter out metrics already added to the activity
	const options = (data?.listContentMetric ?? [])
		.filter(({ id }) => !contentMetricIds.includes(id))
		.map((o) => ({ label: o.metric?.name ?? '', id: o.id }));

	const { mutate: updateActivity } = useApiMutation(updateActivityMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.detail.widget.metrics.add.success' }),
					error: intl.formatMessage({ id: 'journey.detail.widget.metrics.add.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});

	const handleAddMetric = (contentMetricId: string): void => {
		updateActivity({
			activityId,
			data: {
				contentMetrics: [
					{
						connect: {
							id: contentMetricId,
						},
					},
				],
				touchpoint: {
					update: {
						phase: {
							update: {
								contentMetrics: [
									{
										connect: {
											id: contentMetricId,
										},
									},
								],
							},
						},
					},
				},
			},
		});
	};

	const handleCreateMetric = (): void => {
		setModal({ type: 'createMetric', payload: { activityId }, maxWidth: 'xs' });
	};

	return (
		<AddItemSelect
			text={intl.formatMessage({ id: 'journey.detail.widget.metrics.add' })}
			noItemsText={intl.formatMessage({ id: 'journey.detail.widget.metrics.no-metrics' })}
			createText={intl.formatMessage({ id: 'journey.detail.widget.metrics.create' })}
			isLoading={isLoading}
			options={options}
			onChange={handleAddMetric}
			onCreate={handleCreateMetric}
		/>
	);
};
